import { assertDefined } from '../../assertDefined';
import type { DeferredPromise } from '../../deferredPromise';
import { createDeferredPromise } from '../../deferredPromise';
import type { DebouncedMutex } from '../debouncedMutex.types';

/**
 * State for a debounced mutex.
 */
type DebouncedMutexState = {
  /**
   * Next callback to run.
   */
  nextCallback: (() => Promise<void>) | null;
  /**
   * Promise that resolves when the debounced mutex is released.
   */
  queuePromise: Promise<void>;
  /**
   * Promises that allow callers to await the completion of the next callback execution.
   * Each invocation adds a promise to this array, which gets resolved when the callback finishes.
   */
  resolutionPromises: DeferredPromise<void>[];
  /**
   * Timer for the debounced mutex.
   */
  timer: NodeJS.Timeout | null;
};

export const createDebouncedMutex = (): DebouncedMutex => {
  const mutexState = new Map<string, DebouncedMutexState>();

  return async ({ lockKey, callback, debounceTime = 0 }) => {
    const resolutionPromise = createDeferredPromise<void>();

    // Prefill the state for this key if it doesn't exist to avoid needing to check for existence later.
    if (!mutexState.has(lockKey)) {
      mutexState.set(lockKey, {
        nextCallback: null,
        queuePromise: Promise.resolve(),
        resolutionPromises: [],
        timer: null,
      });
    }

    const currentState = mutexState.get(lockKey);

    // This should never happen, but we'll assert it for TS safety.
    assertDefined(currentState, `Mutex state for key ${lockKey} not found`);

    currentState.resolutionPromises.push(resolutionPromise);

    if (currentState.timer) {
      clearTimeout(currentState.timer);
    }

    // The last invocation wins. Always.
    currentState.nextCallback = callback;

    const addCallbackToQueue = async () => {
      currentState.queuePromise = currentState.queuePromise.then(async () => {
        if (!currentState.nextCallback) {
          return;
        }

        // We can't just use the callback param directly because later invocations
        // should override previous ones.
        // Scenario to illustrate this (none of these will use the timer param for simplicity):
        // 1. Very long running promise callback A runs
        // 2. While it's running, we invoke callback B, which queues itself with "then" and overrides nextCallback with B.
        // 3. While A is still running, we invoke callback C, same thing happens, now nextCallback is C.
        // 4. A resolves, nextCallback is invoked with C.
        // 5. After C resolves, nextCallback is invoked with null, so it doesn't run.
        // Conclusion: B was never executed and only the last invocation runs.
        const { nextCallback } = currentState;

        currentState.nextCallback = null;

        const promisesToResolve = currentState.resolutionPromises;

        currentState.resolutionPromises = [];

        try {
          await nextCallback();

          promisesToResolve.forEach((resolutionPromise) =>
            resolutionPromise.resolve()
          );
        } catch (error) {
          promisesToResolve.forEach((resolutionPromise) =>
            resolutionPromise.reject(error)
          );
        }
      });
    };

    if (debounceTime > 0) {
      currentState.timer = setTimeout(addCallbackToQueue, debounceTime);
    } else {
      await addCallbackToQueue();
    }

    return resolutionPromise.promise;
  };
};
